/**
 * Today's leading and lagging smart lists (issue #341).
 *
 * ONE ranking definition, called by the desktop Market Analysis "Lists" card and the mobile
 * Brief's list-mover cards, so the two surfaces can never name different lists. It ranks the
 * `/list-performance` map the browser already holds (`useListPerformance`) — no new request.
 *
 * Three exclusions make the ranking mean something (measured 2026-09-16 on the raw map):
 *  - Only lists the picker renders are eligible (`publicListNames` from `useCategories`).
 *    The public map also carries `portfolio_*` / `ai_*` user lists — a 3-stock portfolio at
 *    +5.2% was #2 — and the user's own watchlist, labels and strategies never compete.
 *  - Lists DEFINED by today's move (`MOVE_DEFINED_LISTS`) win every day by construction and
 *    say nothing: `small_cap_gainers` +6.2%, `day_gainers` +4.3%, `day_losers` −3.0%.
 *  - A list needs `MIN_STOCKS_WITH_DATA` members priced today and `MIN_BREADTH` of them
 *    moving in its ranked direction, so one +40% ticker cannot carry a list of decliners.
 */

export const LIST_MOVERS_PER_SIDE = 3;
export const MIN_STOCKS_WITH_DATA = 5;
export const MIN_BREADTH = 0.6;

/**
 * Lists whose CRITERIA are the daily move itself. Kept explicit rather than inferred from
 * the description text — "biggest daily price gains" flags `day_gainers` but `low_volume_drops`
 * reads "bounce candidates", and `most_actives` / `trending` are volume- and attention-defined
 * and stay eligible.
 */
export const MOVE_DEFINED_LISTS = new Set([
  'day_gainers',
  'day_losers',
  'small_cap_gainers',
  'extended_hours_movers',
  'low_volume_drops',
]);

/**
 * id -> display name for every list the My Lists picker renders. A list sitting in several
 * categories (`bullish_trend` is in Swing AND Technical) is one entry; the `personal`
 * category has no filters, so nothing user-owned gets in.
 */
export const publicListNames = (categories) => {
  const names = new Map();
  for (const category of Array.isArray(categories) ? categories : []) {
    for (const filter of category?.filters || []) {
      if (filter?.id && !names.has(filter.id)) names.set(filter.id, filter.name || filter.id);
    }
  }
  return names;
};

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * @returns {{ leaders: Entry[], laggards: Entry[] }} — each side sorted best-first (leaders
 *   descending, laggards ascending), at most `perSide` entries, possibly empty.
 *   Entry: { id, name, avgChange, gainers, losers, stocksWithData }.
 */
export const rankListMovers = (performanceData, categories, {
  perSide = LIST_MOVERS_PER_SIDE,
  minStocks = MIN_STOCKS_WITH_DATA,
  minBreadth = MIN_BREADTH,
  exclude = MOVE_DEFINED_LISTS,
} = {}) => {
  const names = publicListNames(categories);
  const leaders = [];
  const laggards = [];

  for (const [id, name] of names) {
    if (exclude.has(id)) continue;
    const perf = performanceData?.[id];
    if (!perf || !isFiniteNumber(perf.avgChange)) continue;
    const { avgChange, gainers = 0, losers = 0, stocksWithData = 0 } = perf;
    if (!isFiniteNumber(stocksWithData) || stocksWithData < minStocks) continue;

    const entry = { id, name, avgChange, gainers, losers, stocksWithData };
    if (avgChange > 0 && gainers / stocksWithData >= minBreadth) leaders.push(entry);
    else if (avgChange < 0 && losers / stocksWithData >= minBreadth) laggards.push(entry);
  }

  leaders.sort((a, b) => b.avgChange - a.avgChange);
  laggards.sort((a, b) => a.avgChange - b.avgChange);
  return { leaders: leaders.slice(0, perSide), laggards: laggards.slice(0, perSide) };
};

export const hasListMovers = (movers) =>
  !!movers && (movers.leaders.length > 0 || movers.laggards.length > 0);

/** "8↑ / 2↓" — the breadth behind the average, same glyphs as the list badge's hover. */
export const breadthText = (entry) => `${entry.gainers}↑ / ${entry.losers}↓`;

/** "+2.7%" / "−0.9%" — one signed format for both surfaces, so the same list reads the same. */
export const formatListMove = (avgChange) =>
  `${avgChange > 0 ? '+' : ''}${avgChange.toFixed(1)}%`;
